import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Slider,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { useDispatch, useSelector } from 'react-redux';
import { updateEdge } from '../features/networkUndoSlice';
import { setSelectedEdge } from '../features/networkSlice';
import {
  POSITIVE, NEGATIVE, DEFAULT_LOOP_ANGLE, sameId,
} from '../lib/model';
import ColorField from './common/ColorField';
import { COLORS } from '../lib/network/constants';

const toDegrees = (radians) => Math.round((radians * 180) / Math.PI);
const toRadians = (degrees) => (degrees * Math.PI) / 180;

export default function EditEdgeDialog() {
  const dispatch = useDispatch();
  const selectedEdge = useSelector((state) => state.network.selectedEdge);
  const nodes = useSelector((state) => state.networkUndo.present.nodes);

  const [formData, setFormData] = useState({});

  useEffect(() => setFormData(selectedEdge), [selectedEdge]);

  const reset = () => {
    dispatch((setSelectedEdge(null)));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'sign') {
      setFormData({ ...formData, sign: Number(value) });
    } else if (name === 'dashes') {
      setFormData({ ...formData, dashes: e.target.checked });
    } else if (name === 'color') {
      setFormData({ ...formData, color: value || null });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSave = () => {
    const cleaned = { ...formData };
    if (!cleaned.color) cleaned.color = null;
    if (!cleaned.dashes) cleaned.dashes = false;
    if (!cleaned.title) cleaned.title = null;
    dispatch(updateEdge(cleaned));
    reset();
  };

  const roundness = formData?.roundness || 0;
  const isLoop = formData && sameId(formData.from, formData.to);

  return (
    <Dialog open={selectedEdge !== null} onClose={reset} fullWidth maxWidth="md">
      {formData && (
        <form
          autoComplete="off"
          action="/"
          method="POST"
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
        >
          <DialogTitle>Edit Link</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <FormControl sx={{ m: 1 }} size="small">
                <InputLabel id="from-select">From</InputLabel>
                <Select
                  name="from"
                  labelId="from-select"
                  value={formData.from ?? ''}
                  label="From"
                  onChange={handleChange}
                >
                  {nodes.map((node) => (
                    <MenuItem value={node.id} key={`from-${node.id}`}>
                      {`${node.label} (${node.id})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ m: 1 }} size="small">
                <InputLabel id="to-select">To</InputLabel>
                <Select
                  name="to"
                  labelId="to-select"
                  value={formData.to ?? ''}
                  label="To"
                  onChange={handleChange}
                >
                  {nodes.map((node) => (
                    <MenuItem value={node.id} key={`to-${node.id}`}>
                      {`${node.label} (${node.id})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isLoop && (
                <FormControl sx={{ m: 1 }} size="small">
                  <InputLabel shrink htmlFor="self-reference-angle-slider">
                    Self Effect Position
                  </InputLabel>
                  <Slider
                    name="loopAngle"
                    id="self-reference-angle-slider"
                    value={toDegrees(formData.loopAngle ?? DEFAULT_LOOP_ANGLE)}
                    min={0}
                    max={360}
                    step={5}
                    onChange={(e, value) => setFormData({ ...formData, loopAngle: toRadians(value) })}
                    valueLabelDisplay="off"
                    marks={[
                      { value: 0, label: 'E' },
                      { value: 90, label: 'N' },
                      { value: 180, label: 'W' },
                      { value: 270, label: 'S' },
                    ]}
                    aria-labelledby="self-reference-angle-slider"
                  />
                </FormControl>
              )}
              <TextField
                fullWidth
                size="small"
                multiline
                minRows="3"
                maxRows="10"
                value={formData.title || ''}
                name="title"
                label="Title"
                onChange={handleChange}
              />
              <FormControl sx={{ m: 1 }} size="small">
                <InputLabel id="sign-select">Type</InputLabel>
                <Select
                  name="sign"
                  labelId="sign-select"
                  value={formData.sign ?? ''}
                  label="Type"
                  onChange={handleChange}
                >
                  <MenuItem value={NEGATIVE}>Negative</MenuItem>
                  <MenuItem value={POSITIVE}>Positive</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={formData.dashes || false}
                    name="dashes"
                    onChange={handleChange}
                  />
                )}
                label="Dashes"
              />
              <ColorField
                label="Colour"
                name="color"
                value={formData.color || ''}
                fallback={COLORS.edge}
                onChange={handleChange}
              />
              {!isLoop && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1, m: 1,
                }}
                >
                  <FormControl sx={{ flex: 1 }} size="small">
                    <InputLabel shrink htmlFor="curve-slider">Curve</InputLabel>
                    <Slider
                      name="roundness"
                      id="curve-slider"
                      value={roundness}
                      min={-1}
                      max={1}
                      step={0.01}
                      onChange={(e, value) => setFormData({ ...formData, roundness: value })}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => value.toFixed(2)}
                      marks={[-1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1].map((value) => ({
                        value, label: String(value),
                      }))}
                      aria-labelledby="curve-slider"
                    />
                  </FormControl>
                  <Tooltip title={roundness >= 0 ? 'Counter clockwise' : 'Clockwise'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={roundness === 0}
                        onClick={() => setFormData({ ...formData, roundness: -roundness })}
                      >
                        {roundness >= 0 ? <RotateLeftIcon /> : <RotateRightIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={reset}>Cancel</Button>
            <Button variant="contained" type="submit">Save</Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
